import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import AdminFullTable from '../../components/AdminFullTable';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Dashboard.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API_BASE_URL from '../../config';
import '../../axios_overrite';

const API_URL = process.env.REACT_APP_API_URL || API_BASE_URL;

// ── Delete Confirmation Modal ──────────────────────────────
function DeleteConfirmModal({ open, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="delete-modal-overlay">
      <div className="delete-modal-content">
        <div className="delete-modal-icon">⚠️</div>
        <h2 className="delete-modal-title">Delete Information</h2>
        <div className="delete-modal-desc">This record will be permanently deleted.</div>
        <div className="delete-modal-actions">
          <button className="delete-modal-cancel" onClick={onCancel}>No, Keep it</button>
          <button className="delete-modal-confirm" onClick={onConfirm}>Yes, Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────
export default function AdminSubscribers() {
  const [data,            setData]            = useState([]);
  const [search,          setSearch]          = useState('');
  const [page,            setPage]            = useState(1);
  const [total,           setTotal]           = useState(0);
  const [limit,           setLimit]           = useState(25);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId,        setDeleteId]        = useState(null);
  const [exporting,       setExporting]       = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page, search, limit]);

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/subscribers?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
      );
      setData(res.data.subscribers || []);
      setTotal(res.data.total      || 0);
    } catch {
      setData([]);
      setTotal(0);
    }
  };

  const handleDeleteClick   = (id) => { setDeleteId(id); setDeleteModalOpen(true); };
  const handleDeleteCancel  = ()   => { setDeleteModalOpen(false); setDeleteId(null); };

  const handleDeleteConfirm = async () => {
    await axios.delete(`${API_URL}/api/admin/insights/${deleteId}`);
    setDeleteModalOpen(false);
    setDeleteId(null);
    fetchData();
    toast.success('Subscriber deleted successfully!');
  };

  // ── Export to a real .xlsx file (built client-side with SheetJS) ──
  const handleExport = async () => {
    setExporting(true);
    try {
      // Pull ALL matching rows (not just the current page) so the Excel file
      // reflects the full search result, same as the old CSV export did.
      const res = await axios.get(
        `${API_URL}/api/subscribers?page=1&limit=100000&search=${encodeURIComponent(search)}`
      );
      const rows = res.data.subscribers || [];

      if (rows.length === 0) {
        toast.info('No records to export.');
        setExporting(false);
        return;
      }

      // Shape the rows into clean, human-readable columns for Excel
      const excelRows = rows.map((row, index) => ({
        'S.NO': index + 1,
        'Email': row.email || '',
        'Subscribed At': row.created_at || ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelRows);

      // Reasonable column widths so it's readable on open
      worksheet['!cols'] = [
        { wch: 6 }, { wch: 30 }, { wch: 20 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Subscribers');

      XLSX.writeFile(workbook, 'subscribers.xlsx');
      toast.success('Excel file downloaded!');
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const tableData = data.map((row, index) => ({
    id: (page - 1) * limit + index + 1,
    email: row.email,
    created_at: row.created_at,
    actions: (
      <div className="insights-admin-actions">
        <button
          className="insights-admin-delete-btn"
          onClick={() => handleDeleteClick(row.id)}
        >
          <img src="/icons/delete.png" alt="delete" className="table-action-icon" />
          Delete
        </button>
      </div>
    ),
  }));

  const columns = [
    { key: 'id',         label: 'S.NO' },
    { key: 'email',      label: 'Email' },
    { key: 'created_at', label: 'Subscribed At' },
    { key: 'actions',    label: 'Actions' },
  ];

  const totalPages   = Math.ceil(total / limit);
  const visiblePages = [];
  for (let i = 1; i <= totalPages && i <= 5; i++) visiblePages.push(i);

  return (
    <AdminLayout>
      <div className="insights-admin-section">
        <div className="insights-admin-container">
          <h2 className="insights-admin-title">Subscribers</h2>
          <div className="insights-admin-subtitle">Forms and Submissions</div>

          {/* Table header controls */}
          <div className="insights-admin-table-header">
            <div className="insights-admin-table-header-left">
              <div className="insights-admin-show-entries">
                <label htmlFor="show-entries" className="insights-admin-show-entries-label">
                  Show Entries :
                </label>
                <select
                  id="show-entries"
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="insights-admin-show-entries-select"
                >
                  {[10, 25, 50, 100].map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <span className="insights-admin-count-badge">Total Count : {total}</span>

              <button className="insights-admin-export-btn" onClick={handleExport} disabled={exporting}>
                <img src="/icons/excel.png" alt="Export" className="table-action-icon" />
                {exporting ? 'Exporting...' : 'Export To Excel'}
              </button>
            </div>

            <div className="insights-admin-table-header-right">
              <div className="insights-admin-search-wrapper">
                <span className="insights-admin-search-icon">
                  <img src="/icons/search.png" alt="search" />
                </span>
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="insights-admin-table-search"
                />
              </div>
            </div>
          </div>

        
          <div className="insights-admin-table-wrapper">
            <AdminFullTable
              data={tableData}
              columns={columns}
              tableClass="insights-admin-table"
            />

            {/* Footer: info + pagination */}
            <div className="insights-admin-table-footer">
              <div className="insights-admin-info">
                {total > 0
                  ? `Showing ${((page - 1) * limit) + 1} to ${Math.min(page * limit, total)} of ${total} entries`
                  : 'Showing 0 to 0 of 0 entries'}
              </div>

              <div className="insights-admin-pagination">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="insights-admin-page-btn"
                >
                  ‹
                </button>

                {visiblePages.map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`insights-admin-page-btn${page === pageNum ? ' active' : ''}`}
                  >
                    {pageNum}
                  </button>
                ))}

                {totalPages > 5 && (
                  <>
                    <span className="pagination-ellipsis">…</span>
                    <button
                      onClick={() => setPage(totalPages)}
                      className={`insights-admin-page-btn${page === totalPages ? ' active' : ''}`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  disabled={page === totalPages || total === 0}
                  onClick={() => setPage(page + 1)}
                  className="insights-admin-page-btn"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteModalOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      <ToastContainer position="top-right" autoClose={3000} />
    </AdminLayout>
  );
}